import { InquiryStatusEnum, KYBProgressStatusEnum } from 'anchor-kyb-service/types';
import { useOverlayContext } from 'seagull';
import { useIoContext, useStoreActions, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { KycCompletionStatus } from '~/store/store.model';
const personaInquiryLoader = 'persona-inquiry';
export function shouldStartPersonaKYC(param) {
    let { personaKycStatus, stripeKycStatus } = param;
    const hasPersonaFailed = personaKycStatus === KYBProgressStatusEnum.Declined || personaKycStatus === KYBProgressStatusEnum.Errored;
    const neverStartedStripeKYC = stripeKycStatus === KycCompletionStatus.InitialIncomplete;
    const isPendingMigrationFromStripeToPersona = stripeKycStatus === KycCompletionStatus.Completed && personaKycStatus != null && [
        KYBProgressStatusEnum.NotStarted,
        KYBProgressStatusEnum.DetailsNeeded
    ].includes(personaKycStatus);
    return hasPersonaFailed || neverStartedStripeKYC || isPendingMigrationFromStripeToPersona;
}
export function useStartKYC() {
    const { personaClient, openNewTab } = useIoContext();
    const { api, fetchPersonaKYBStatus } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { getState } = useStoreStatic();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { KYB_UPDATED_FLOW } = useFeatureFlags();
    async function startKYCPersona(businessId) {
        startLoader(personaInquiryLoader);
        const inquiryRequest = KYB_UPDATED_FLOW ? api.vendor.getKYBUpdatedFlowInquiry(businessId) : api.vendor.getKYBInquiryWithSelfie(businessId);
        const result = await handlePromise(inquiryRequest);
        if (result.success) {
            const { inquiryId, sessionToken, inquiryStatus } = result.data;
            if (InquiryStatusEnum.NeedsReview === inquiryStatus) {
                await fetchPersonaKYBStatus();
                stopLoader(personaInquiryLoader);
                return;
            }
            const client = personaClient({
                onError: ()=>stopLoader(personaInquiryLoader),
                onReady: ()=>stopLoader(personaInquiryLoader),
                onComplete: ()=>fetchPersonaKYBStatus()
            });
            client.open(inquiryId, sessionToken);
        } else {
            showErrorToast(commonTexts.toasts.failedToStartKybProcess());
            stopLoader(personaInquiryLoader);
        }
    }
    async function startKYCStripe(businessId) {
        const url = await api.vendor.getStripeHostedKYBUrl(businessId);
        openNewTab(url);
    }
    async function startKYC() {
        var _getState_personaKYBStatus_data;
        const worksAt = selectors.user.business(getState());
        const businessId = worksAt === null || worksAt === void 0 ? void 0 : worksAt.id;
        const stripeKycStatus = worksAt === null || worksAt === void 0 ? void 0 : worksAt.kycCompletionStatus;
        const personaKycStatus = (_getState_personaKYBStatus_data = getState().personaKYBStatus.data) === null || _getState_personaKYBStatus_data === void 0 ? void 0 : _getState_personaKYBStatus_data.status;
        if (businessId == null) {
            return;
        }
        if (shouldStartPersonaKYC({
            personaKycStatus,
            stripeKycStatus
        })) {
            startKYCPersona(businessId);
        } else {
            startKYCStripe(businessId);
        }
    }
    return {
        startKYC
    };
}
